<template>
	<div class="flex flex-col h-full mini-cart__upsell-item gap-2">
		<div
			v-if="!product.available"
			class="mb-2 text-red-600"
		>
			<strong>{{ soldOutText }}</strong>
		</div>

		<figure class="flex-1 mini-cart__upsell-fig">
			<a
				:href="product.url"
				:aria-label="`Permalink to ${product.title}`"
			>
				<img
					class="object-cover h-full w-full mini-cart__upsell-img"
					:srcset="getCroppedImageUrl(product.featured_image, [120, 160, 240, 320])"
					:src="getCroppedImageUrl(product.featured_image, 160)"
					:alt="product.title"
					width="160"
					height="200"
					loading="lazy"
				/>
			</a>
		</figure>
		<div class="text-xs flex-1 flex flex-col justify-between mini-cart__upsell-desc">
			<div>
				<h3>
					<a :href="product.url">
						{{ product.title }}
					</a>
				</h3>
			</div>
			<p class="">
				{{ formatMoney(product.price) }}
			</p>
		</div>
		<form @submit.prevent="addUpsellProductToCart($event, product)"
		class="grid gap-2">
			<input
				type="hidden"
				:value="selectedVariantIds[product.id]"
			>
			<select
			 	v-if="!(product.variants.length === 1 && product.variants[0].title === 'Default Title')"
				name="id"
				v-model="selectedVariantIds[product.id]"
				:id="`productSelect--${product.id}`"
				class="w-full transition-opacity mini-cart__select"
				:class="{ 'opacity-50': isAnyLoading() && !isLoading(product.id) }"
				:disabled="isLoading(product.id) || isAnyLoading()"
			>
				<option
					v-for="variant in product.variants"
					:key="variant.id"
					:value="variant.id"
					:disabled="!variant.available"
				>
					{{ variant.title }} - {{ formatMoney(variant.price) }}
				</option>
			</select>
			<input 
				v-else 
				type="hidden" 
				name="id" 
				:value="product.variants[0].id" 
				:disabled="!product.variants[0].available"
			>

			<div class="items-center space-x-2 sr-only">
				<label
					:for="`Quantity--${product.id}`"
					class="text-sm"
				>
					{{ quantityLabelText }}
				</label>
				<input
					type="number"
					:id="`Quantity--${product.id}`"
					name="quantity"
					:value="getMinQty(product)"
					:min="getMinQty(product)"
				/>
			</div>
			<button
				type="submit"
				name="add"
				class="mini-cart__button"
				:disabled="isLoading(product.id) || isAnyLoading()"
				:class="{ 
					'cursor-not-allowed': isLoading(product.id) || isAnyLoading(),
					'!opacity-50': isAnyLoading() && !isLoading(product.id)
				}"
			>
				{{ isLoading(product.id) ? itemIsAddingText : addtoBagText }}
			</button>
		</form>
	</div>
</template>

<script setup>
import formatMoney from '../helpers/format-money.js';
import { getCroppedImageUrl } from '../helpers/get-cropped-image-url.js';

// Props
const props = defineProps({
	product: {
		type: Object,
		required: true
	},
	selectedVariantIds: {
		type: Object,
		required: true
	},
	soldOutText: {
		type: String,
		default: 'Sold out'
	},
	addtoBagText: {
		type: String,
		default: 'Add to cart'
	},
	itemIsAddingText: {
		type: String,
		default: 'Adding...'
	},
	quantityLabelText: {
		type: String,
		default: 'Quantity of'
	},
	isLoading: {
		type: Function,
		required: true
	},
	isAnyLoading: {
		type: Function,
		required: true
	}
});

// Emits
const emit = defineEmits(['add-upsell-product']);

// Methods
const addUpsellProductToCart = (event, product) => {
	emit('add-upsell-product', event, product);
};


const getSelectedVariant = (product) => {
  const selectedId = props.selectedVariantIds[product.id];
  return product.variants.find(v => v.id === selectedId) || product.variants[0];
};

const getMinQty = (product) => {
  const variant = getSelectedVariant(product);
  return variant?.quantity_rule?.min ?? 1;
};
</script>
